import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import ComingSoon from '../components/ComingSoon';
import { Loader2, Upload, Download, BarChart3, Shield, Clock, HardDrive, MessageSquare } from 'lucide-react';

export default function Login() {
  const { login, handleCallback, isLoading, isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const error = searchParams.get('error');

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
      return;
    }

    if (error) {
      console.error('OAuth error:', error);
      return;
    }

    if (code && state) {
      handleCallback(code, state).then(() => {
        navigate('/', { replace: true });
      }).catch((error) => {
        console.error('Callback error:', error);
      });
    }
  }, [code, state, error, isAuthenticated, handleCallback, navigate]);

  const handleLogin = async () => {
    try {
      await login();
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  if (code && state) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <Loader2 className="mx-auto h-12 w-12 text-twitch-600 animate-spin" />
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Completing sign in...
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Please wait while we verify your Twitch account.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="flex items-center space-x-3">
              <div className="text-6xl">📹</div>
              <div className="text-left">
                <h1 className="text-4xl font-bold text-gray-900">
                  Crafting Chaos Gaming
                </h1>
                <p className="text-xl text-twitch-600 font-semibold">
                  Twitch VOD Backups
                </p>
              </div>
            </div>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Professional VOD archiving platform for Twitch streamers. 
            Secure, automated, and reliable backup solution for your valuable content.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* Features Section */}
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Platform Features</h2>
            
            <div className="grid gap-4">
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-start space-x-3">
                  <Upload className="h-6 w-6 text-blue-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Manual Upload</h3>
                    <p className="text-sm text-gray-600">Upload your own recordings up to 100GB per file (.mp4, .mkv)</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm relative">
                <ComingSoon size="sm" />
                <div className="flex items-start space-x-3">
                  <Download className="h-6 w-6 text-green-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Automatic VOD Sync</h3>
                    <p className="text-sm text-gray-600">Premium users get automated backup every 3 hours from Twitch</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-start space-x-3">
                  <MessageSquare className="h-6 w-6 text-cyan-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Chat Download</h3>
                    <p className="text-sm text-gray-600">Download Twitch chat logs as JSON files alongside your VODs</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-start space-x-3">
                  <BarChart3 className="h-6 w-6 text-purple-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Analytics Dashboard</h3>
                    <p className="text-sm text-gray-600">Track storage usage, backup statistics, and data transfer</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-start space-x-3">
                  <Shield className="h-6 w-6 text-red-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Secure Storage</h3>
                    <p className="text-sm text-gray-600">Cloudflare R2 storage with automatic preview generation</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-start space-x-3">
                  <Clock className="h-6 w-6 text-orange-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Retention Policies</h3>
                    <p className="text-sm text-gray-600">Default: 1 year retention • Premium: 2 year retention</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-start space-x-3">
                  <HardDrive className="h-6 w-6 text-indigo-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Smart Lifecycle</h3>
                    <p className="text-sm text-gray-600">Automatic long-term tagging after 30 days for cost optimization</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Login Section */}
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 text-center">Get Started</h2>
            
            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
              <div className="text-center space-y-4">
                <div className="flex justify-center">
                  <div className="text-6xl">📹</div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    Connect with Twitch
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Sign in with your Twitch account to start archiving your VODs.
                    Only streamers with broadcasting permissions can access this platform.
                  </p>
                </div>
                <button
                  onClick={handleLogin}
                  disabled={isLoading}
                  className="w-full flex justify-center items-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-twitch-600 hover:bg-twitch-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-twitch-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="animate-spin -ml-1 mr-3 h-4 w-4" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <span className="mr-2">📹</span>
                      Sign in with Twitch
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">Account Types</h4>
              <div className="space-y-2 text-sm text-blue-800">
                <div className="flex justify-between">
                  <span>Default Account:</span>
                  <span>Manual uploads, 1-year retention</span>
                </div>
                <div className="flex justify-between relative">
                  <span>Premium Account:</span>
                  <span>Auto-sync + manual, 2-year retention</span>
                  <ComingSoon size="sm" position="top-right" className="relative -top-1 -right-1" />
                </div>
              </div>
            </div>

            <div className="text-center">
              <div className="space-y-2 text-xs text-gray-500">
                <p>
                  <strong>This is not a Twitch service.</strong> This is an independent platform 
                  operated by Crafting Chaos Gaming for VOD backup purposes.
                </p>
                <p>
                  By signing in, you agree to our terms of service and privacy policy.
                </p>
                <p>
                  Only Twitch streamers with broadcasting permissions can access this platform.
                </p>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-4 mt-6">
            <div className="text-sm text-red-700">
              <strong>Authentication Error:</strong> {error}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}